import React, { useState, useEffect } from "react";

/**
 * PurchaseModal Component - Handles scale NFT purchases
 * Shows different UI based on ownership status
 */
const PurchaseModal = ({ scaleNumber, scale, superscaleIndex, mode, onClose }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proposedName, setProposedName] = useState("");

  useEffect(() => {
    // Check ownership status directly from Zora's public API
    const checkStatus = async () => {
      try {
        // Query Zora's GraphQL API for ownership
        const response = await fetch('https://api.zora.co/graphql', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: `
              query TokenOwnership($contractAddress: String!, $tokenId: String!) {
                token(
                  token: {
                    address: $contractAddress
                    tokenId: $tokenId
                  }
                ) {
                  tokenId
                  owner
                  metadata {
                    name
                    description
                  }
                }
              }
            `,
            variables: {
              contractAddress: "YOUR_ZORA_CONTRACT_ADDRESS",
              tokenId: scaleNumber.toString()
            }
          })
        });
        
        const { data } = await response.json();
        
        if (data?.token?.owner) {
          // Token is owned
          setStatus({
            available: false,
            owner: data.token.owner,
            ownerDisplay: data.token.owner.slice(0, 6) + '...' + data.token.owner.slice(-4),
            approvedName: data.token.metadata?.name,
            namedBy: data.token.metadata?.description // Parse from description if stored there
          });
        } else {
          // Token not yet minted
          setStatus({
            available: true,
            price: "$10"
          });
        }
      } catch (error) {
        console.error("Error checking scale status:", error);
        setStatus({ available: true, price: "$10" });
      } finally {
        setLoading(false);
      }
    };
    
    checkStatus();
  }, [scaleNumber]);

  const handlePurchase = (method) => {
    if (method === 'card') {
      // Crossmint handles everything - just redirect to their hosted checkout
      // After purchase, Crossmint will automatically mint the NFT to the buyer
      const crossmintCheckoutUrl = `https://www.crossmint.com/checkout?` +
        `clientId=YOUR_CROSSMINT_CLIENT_ID&` +
        `mintConfig.type=erc-721&` +
        `mintConfig.contractAddress=YOUR_ZORA_CONTRACT_ADDRESS&` +
        `mintConfig.tokenId=${scaleNumber}&` +
        `successCallbackURL=${encodeURIComponent(window.location.href)}`;
      
      window.location.href = crossmintCheckoutUrl;
    } else if (method === 'wallet') {
      // Direct link to Zora's mint page
      // Zora handles the wallet connection and transaction
      window.open(
        `https://zora.co/collect/base:YOUR_ZORA_CONTRACT_ADDRESS/${scaleNumber}`,
        '_blank'
      );
    }
  };

  const handleMakeOffer = () => {
    // Redirect to Zora marketplace - users can make offers there
    window.open(
      `https://zora.co/collect/base:YOUR_ZORA_CONTRACT_ADDRESS/${scaleNumber}`,
      '_blank'
    );
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="purchase-modal" onClick={(e) => e.stopPropagation()}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="purchase-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        
        {status?.available ? (
          // Available for purchase
          <>
            <h2 className="modal-title">Purchase Scale #{scaleNumber}</h2>
            <div className="modal-scale-info">
              <p className="modal-description">
                Superscale {superscaleIndex}, {mode > 1 ? `${mode}th Mode` : 'Original'}
              </p>
              <p className="modal-intervals">{scale.join('-')}</p>
            </div>

            <div className="modal-price">
              <span className="price-label">Price:</span>
              <span className="price-amount">{status.price || '$10'} USD</span>
            </div>

            <div className="purchase-options">
              <button 
                className="purchase-button purchase-button--card"
                onClick={() => handlePurchase('card')}
              >
                <span>💳</span>
                <div>
                  <div className="button-title">Buy with Credit Card</div>
                  <div className="button-subtitle">No crypto knowledge needed</div>
                </div>
              </button>

              <button 
                className="purchase-button purchase-button--wallet"
                onClick={() => handlePurchase('wallet')}
              >
                <span>🦊</span>
                <div>
                  <div className="button-title">Buy with Wallet</div>
                  <div className="button-subtitle">Connect MetaMask or WalletConnect</div>
                </div>
              </button>
            </div>

            <div className="modal-naming">
              <p className="naming-info">
                After purchase, you'll be able to propose a name for this scale.
              </p>
            </div>
          </>
        ) : (
          // Already owned - show marketplace options
          <>
            <h2 className="modal-title">
              {status.approvedName || `Scale #${scaleNumber}`}
            </h2>
            <div className="modal-scale-info">
              <p className="modal-description">
                Superscale {superscaleIndex}, {mode > 1 ? `${mode}th Mode` : 'Original'}
              </p>
              <p className="modal-intervals">{scale.join('-')}</p>
            </div>

            <div className="ownership-info">
              <p className="ownership-label">Current Owner:</p>
              <p className="ownership-address">{status.ownerDisplay || status.owner}</p>
              {status.approvedName && (
                <p className="ownership-named-by">Named by: {status.namedBy}</p>
              )}
            </div>

            <div className="marketplace-options">
              <p className="marketplace-info">
                This scale has been claimed. You can make an offer to the current owner on Zora.
              </p>
              
              <button 
                className="marketplace-button"
                onClick={handleMakeOffer}
              >
                View on Zora Marketplace
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PurchaseModal;
